import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { StyledButton } from '../styles/StyledButtons';
import ReviewContentEditItemRenderer from './ReviewContentEditItemRenderer';
import {
  REVIEW_CONTENT_TYPE_OPTIONS,
  ReviewContentEditViewProps,
  ReviewContentType,
} from './reviewContentEditViewTypes';
import { getContentTypePickerLabel } from './reviewContentEditViewUtils';

interface ReviewContentEditDialogsProps {
  tone: ReviewContentEditViewProps['tone'];
  modelType: 'FundOpportunity' | 'FundUpdate';
  draftContent: ReviewContentEditViewProps['draftContent'];
  submitting: boolean;
  showCommentDialog: boolean;
  comment: string;
  showAddCommentDialog: boolean;
  addCommentText: string;
  showAddContentDialog: boolean;
  selectedContentType: ReviewContentType | null;
  showReplaceComponentDialog: boolean;
  selectedReplaceContentType: ReviewContentType | null;
  previousComponent: any;
  showPreviousComponentDialog: boolean;
  showOriginalComponentDialog: boolean;
  originalComponentContent: any;
  onCommentChange: (value: string) => void;
  onCommentSubmit: () => void;
  onCommentCancel: () => void;
  onAddCommentTextChange: (value: string) => void;
  onAddCommentSubmit: () => void;
  onAddCommentCancel: () => void;
  onSelectedContentTypeChange: (type: ReviewContentType) => void;
  onAddContentSubmit: () => void;
  onAddContentCancel: () => void;
  onSelectedReplaceContentTypeChange: (type: ReviewContentType) => void;
  onReplaceComponentSubmit: () => void;
  onReplaceComponentCancel: () => void;
  onViewPreviousComponent: () => void;
  onClosePreviousComponent: () => void;
  onCloseOriginalComponent: () => void;
}

function ContentTypePicker({
  selectedType,
  onSelect,
}: {
  selectedType: ReviewContentType | null;
  onSelect: (type: ReviewContentType) => void;
}) {
  return (
    <Stack spacing={1}>
      {REVIEW_CONTENT_TYPE_OPTIONS.map((type) => (
        <Button
          key={type}
          variant={selectedType === type ? 'contained' : 'outlined'}
          onClick={() => onSelect(type)}
          fullWidth
        >
          {getContentTypePickerLabel(type)}
        </Button>
      ))}
    </Stack>
  );
}

export default function ReviewContentEditDialogs({
  tone = 'tone1',
  modelType,
  draftContent,
  submitting,
  showCommentDialog,
  comment,
  showAddCommentDialog,
  addCommentText,
  showAddContentDialog,
  selectedContentType,
  showReplaceComponentDialog,
  selectedReplaceContentType,
  previousComponent,
  showPreviousComponentDialog,
  showOriginalComponentDialog,
  originalComponentContent,
  onCommentChange,
  onCommentSubmit,
  onCommentCancel,
  onAddCommentTextChange,
  onAddCommentSubmit,
  onAddCommentCancel,
  onSelectedContentTypeChange,
  onAddContentSubmit,
  onAddContentCancel,
  onSelectedReplaceContentTypeChange,
  onReplaceComponentSubmit,
  onReplaceComponentCancel,
  onViewPreviousComponent,
  onClosePreviousComponent,
  onCloseOriginalComponent,
}: ReviewContentEditDialogsProps) {
  return (
    <>
      <Dialog open={showCommentDialog} onClose={onCommentCancel} maxWidth="md" fullWidth>
        <DialogTitle>Add Comment for Changes</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              You&apos;ve made changes to this section. Please add a comment explaining your
              proposed modifications.
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comment on your changes *"
            placeholder="Explain what you changed and why..."
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCommentCancel} disabled={submitting}>
            Cancel
          </Button>
          <StyledButton
            onClick={onCommentSubmit}
            disabled={!comment.trim() || submitting}
            variant="contained"
          >
            {submitting ? 'Submitting...' : 'Submit Comment & Save Changes'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog open={showAddCommentDialog} onClose={onAddCommentCancel} maxWidth="md" fullWidth>
        <DialogTitle>Add Comment to Section</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your comment"
            placeholder="Add a comment about this section..."
            value={addCommentText}
            onChange={(e) => onAddCommentTextChange(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onAddCommentCancel} disabled={submitting}>
            Cancel
          </Button>
          <StyledButton
            onClick={onAddCommentSubmit}
            disabled={!addCommentText.trim() || submitting}
            variant="contained"
          >
            {submitting ? 'Adding...' : 'Add Comment'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog open={showAddContentDialog} onClose={onAddContentCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the type of content to add:
          </Typography>
          <ContentTypePicker
            selectedType={selectedContentType}
            onSelect={onSelectedContentTypeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onAddContentCancel} disabled={submitting}>
            Cancel
          </Button>
          <StyledButton
            onClick={onAddContentSubmit}
            disabled={!selectedContentType || submitting}
            variant="contained"
          >
            {submitting ? 'Adding...' : 'Add Content'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReplaceComponentDialog}
        onClose={onReplaceComponentCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Replace Component</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the type of component to replace the current one:
          </Typography>
          <ContentTypePicker
            selectedType={selectedReplaceContentType}
            onSelect={onSelectedReplaceContentTypeChange}
          />
          {previousComponent && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">Previous Component (for reference):</Typography>
                <Button size="small" variant="outlined" onClick={onViewPreviousComponent}>
                  View Full Content
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Type: {previousComponent.type}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onReplaceComponentCancel} disabled={submitting}>
            Cancel
          </Button>
          <StyledButton
            onClick={onReplaceComponentSubmit}
            disabled={!selectedReplaceContentType || submitting}
            variant="contained"
          >
            {submitting ? 'Replacing...' : 'Replace Component'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPreviousComponentDialog}
        onClose={onClosePreviousComponent}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Previous Component Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            This is the content of the component you&apos;re replacing. You can copy text from here
            to use in the new component.
          </Typography>
          {previousComponent && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <ReviewContentEditItemRenderer
                content={previousComponent}
                editMode={false}
                tone={tone}
                draftContent={draftContent}
                modelType={modelType}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClosePreviousComponent}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showOriginalComponentDialog}
        onClose={onCloseOriginalComponent}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Original Published Component</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            This is the original published version of this component. You can copy text from here to
            use in your edits.
          </Typography>
          {originalComponentContent && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <ReviewContentEditItemRenderer
                content={originalComponentContent}
                editMode={false}
                tone={tone}
                draftContent={draftContent}
                modelType={modelType}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseOriginalComponent}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
